/**
 * Factory for a split state/actions text-filter context.
 *
 * State and actions are split into two contexts so the input can update the
 * value without re-rendering every actions consumer — only components that
 * actually read the value re-render on each keystroke.
 *
 * `useTransition` wraps the value commit so typing stays responsive while
 * expensive derived filtering runs at lower priority.
 *
 * Keep hooks and components in separate modules so the component module remains
 * a valid Fast Refresh boundary:
 *
 * Context.ts:
 *   export const myFilter = createTextFilterContext();
 *   export const useMyFilter = myFilter.useFilter;
 *   export const useMyFilterActions = myFilter.useFilterActions;
 *
 * ContextComponents.tsx:
 *   import { myFilter } from "./Context";
 *   export const MyProvider = myFilter.Provider;
 *   // In a component:
 *   export const MyFilterInput = () => {
 *     const { t } = useLingui();
 *     return <myFilter.FilterInput label={t`...`} />;
 *   };
 */
import ControlledInput from "@medoma/components/InputField/ControlledInput";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useTransition,
} from "react";

type TextFilterState = {
  value: string;
  /** Bumped on external clears so `FilterInput` can reset local state during render (no effect). */
  inputResetKey: number;
};

type TextFilterActions = {
  setFilterValue: (value: string) => void;
  clearFilterValue: () => void;
};

export const createTextFilterContext = () => {
  const StateContext = createContext<TextFilterState>({
    value: "",
    inputResetKey: 0,
  });

  const ActionsContext = createContext<TextFilterActions>({
    setFilterValue: () => {},
    clearFilterValue: () => {},
  });

  const Provider = ({ children }: { children: React.ReactNode }) => {
    const [value, setValue] = useState("");
    const [inputResetKey, setInputResetKey] = useState(0);
    const [, startTransition] = useTransition();

    // Filtering on each keystroke is expensive; mark the commit as a
    // transition so typing stays responsive while derived work runs at lower
    // priority.
    const setFilterValue = useCallback((next: string) => {
      startTransition(() => setValue(next));
    }, []);

    // Clearing is an explicit user action, run it synchronously and signal
    // the input to reset its local value.
    const clearFilterValue = useCallback(() => {
      setValue("");
      setInputResetKey((k) => k + 1);
    }, []);

    // Memoized so the actions context keeps a stable reference across value
    // updates — this lets actions-only subscribers skip re-renders.
    const actions = useMemo(
      () => ({ setFilterValue, clearFilterValue }),
      [setFilterValue, clearFilterValue],
    );

    const state = useMemo(
      () => ({ value, inputResetKey }),
      [value, inputResetKey],
    );

    return (
      <ActionsContext.Provider value={actions}>
        <StateContext.Provider value={state}>{children}</StateContext.Provider>
      </ActionsContext.Provider>
    );
  };

  const useFilter = () => useContext(StateContext);

  /** Stable actions object — does not re-render when the filter value changes. */
  const useFilterActions = () => useContext(ActionsContext);

  /**
   * Controlled search input that mirrors context state.
   *
   * Local state drives the input so each keystroke paints immediately. The
   * same text is committed through context (`setFilterValue`), which the
   * provider wraps in `startTransition` so expensive filtering stays off the
   * urgent path.
   *
   * When `inputResetKey` bumps after a clear, local state is synced during
   * render (no effect).
   */
  const FilterInput = ({ label }: { label: string }) => {
    const { setFilterValue } = useFilterActions();
    const { inputResetKey } = useFilter();
    const [localValue, setLocalValue] = useState("");
    const [prevResetKey, setPrevResetKey] = useState(inputResetKey);

    if (prevResetKey !== inputResetKey) {
      setPrevResetKey(inputResetKey);
      setLocalValue("");
    }

    return (
      <ControlledInput
        type="search"
        label={label}
        hideLabel
        showLabelAsPlaceholder
        size="compact"
        value={localValue}
        onChange={(e) => {
          const next = e.target.value;
          setLocalValue(next);
          setFilterValue(next);
        }}
      />
    );
  };

  /**
   * Returns a `FilterBanner` filter item when the value is non-empty, or
   * `undefined` when the filter is inactive.
   *
   * @param name - The human-readable filter name shown in the banner.
   */
  const useFilterBannerItem = (name: string) => {
    const { value } = useFilter();
    const { clearFilterValue } = useFilterActions();
    return value !== ""
      ? { name, value: `"${value}"`, onClear: clearFilterValue }
      : undefined;
  };

  return {
    Provider,
    useFilter,
    useFilterActions,
    FilterInput,
    useFilterBannerItem,
  };
};
